import {
  Injectable,
  Type,
  Injector,
  createComponent,
  ComponentRef,
  ViewContainerRef,
  EnvironmentInjector,
} from '@angular/core';
import { Subject } from 'rxjs';

export class AppModalOverlayRef<R = any> {
  /** Payload passed to the opened component (legacy B3ModalService compatible). */
  data: any = {};
  size: '' | 'small' | 'large' | 'extra-large' | 'sm' | 'md' | 'lg' = '';
  /**
   * Emits this overlay ref on close (legacy shape: result.data.*).
   */
  readonly afterClosed$ = new Subject<AppModalOverlayRef<R>>();

  private closed = false;
  private onDestroy?: () => void;

  /** @internal */
  setDestroyHandler(handler: () => void): void {
    this.onDestroy = handler;
  }

  close(result?: R): void {
    if (this.closed) {
      return;
    }
    this.closed = true;
    if (result !== undefined) {
      this.data = result;
    }
    this.afterClosed$.next(this);
    this.afterClosed$.complete();
    this.onDestroy?.();
  }
}

/**
 * Imperative modal opener using new DS &lt;b3-modal&gt; host components.
 * Replaces @b3/design-system-lib B3ModalService for app call sites.
 */
@Injectable({ providedIn: 'root' })
export class AppModalService {
  private host?: ViewContainerRef;
  private readonly openRefs = new Set<ComponentRef<unknown>>();

  constructor(
    private readonly injector: Injector,
    private readonly environmentInjector: EnvironmentInjector,
  ) {}

  /** Called once from AppComponent after view init. */
  registerHost(host: ViewContainerRef): void {
    this.host = host;
  }

  open<R = any, C = unknown>(
    component: Type<C>,
    data: any = {},
  ): AppModalOverlayRef<R> {
    const overlayRef = new AppModalOverlayRef<R>();
    overlayRef.data = data ?? {};
    overlayRef.size = (data?.size as AppModalOverlayRef['size']) ?? '';

    const elementInjector = Injector.create({
      providers: [
        { provide: AppModalOverlayRef, useValue: overlayRef },
      ],
      parent: this.injector,
    });

    let componentRef: ComponentRef<C>;

    if (this.host) {
      componentRef = this.host.createComponent(component, {
        injector: elementInjector,
        environmentInjector: this.environmentInjector,
      });
    } else {
      componentRef = createComponent(component, {
        environmentInjector: this.environmentInjector,
        elementInjector,
      });
      document.body.appendChild(componentRef.location.nativeElement);
    }

    this.openRefs.add(componentRef as ComponentRef<unknown>);

    overlayRef.setDestroyHandler(() => {
      this.openRefs.delete(componentRef as ComponentRef<unknown>);
      componentRef.destroy();
    });

    return overlayRef;
  }
}
